import { CheckCircle2, ShieldCheck, Zap } from "lucide-react";

export default function WhatIsAnterPassage() {
  return (
    <section id="what-is-anter" className="py-16">
      <div className="glass-panel relative overflow-hidden rounded-2xl border border-primary-border/40 p-8 sm:p-10">
        {/* Subtle ambient glow behind passage */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/10 blur-3xl"
        />

        <div className="relative z-10 max-w-4xl">
          <div className="mb-4 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-border/60 bg-primary-dim px-3 py-1 text-xs font-semibold text-primary-text">
              <Zap className="h-3.5 w-3.5" /> Platform Overview
            </span>
            <time dateTime="2026-09-17" className="font-mono text-xs text-text-faint">
              Last updated: September 2026
            </time>
          </div>

          {/* H2 Literal Match for Query "What is Anter" */}
          <h2 className="mb-4 text-2xl font-extrabold tracking-tight text-text sm:text-3xl">
            What is Anter?
          </h2>

          {/* 1-2 Sentence Direct Definition Answer for Passage Indexing */}
          <p className="mb-6 text-base leading-relaxed text-text sm:text-lg">
            <strong>Anter</strong> is an enterprise-grade{" "}
            <a
              href="/why-anter"
              className="text-primary-text underline decoration-primary/40 hover:decoration-primary"
            >
              managed AI agents platform
            </a>{" "}
            and runtime system for building, orchestrating, and shipping autonomous multi-agent
            systems as production streaming APIs. It combines visual agent building with
            deterministic routing, multi-LLM support, and end-to-end execution tracing without
            requiring teams to build custom hosting infrastructure.
          </p>

          {/* Self-contained claims + evidence bullets */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-border/60 bg-bg-secondary/40 p-4">
              <div className="mb-2 flex items-center gap-2 font-bold text-text">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                <span className="text-sm">Multi-Agent Engine</span>
              </div>
              <p className="text-xs leading-relaxed text-text-muted">
                Route complex workflows across specialized{" "}
                <a
                  href="/glossary/sub-agent"
                  className="text-primary-text underline decoration-primary/30"
                >
                  sub-agents
                </a>{" "}
                using validated{" "}
                <a
                  href="/glossary/agent-transfer"
                  className="text-primary-text underline decoration-primary/30"
                >
                  transfer
                </a>{" "}
                and delegation protocols with auditability.
              </p>
            </div>

            <div className="rounded-xl border border-border/60 bg-bg-secondary/40 p-4">
              <div className="mb-2 flex items-center gap-2 font-bold text-text">
                <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
                <span className="text-sm">MCP & Tool Routing</span>
              </div>
              <p className="text-xs leading-relaxed text-text-muted">
                Integrate Model Context Protocol (
                <a
                  href="/solutions/mcp-agent-platform"
                  className="text-primary-text underline decoration-primary/30"
                >
                  MCP servers
                </a>
                ) and REST APIs with zero-trust permission modes and execution step budgets.
              </p>
            </div>

            <div className="rounded-xl border border-border/60 bg-bg-secondary/40 p-4">
              <div className="mb-2 flex items-center gap-2 font-bold text-text">
                <Zap className="h-4 w-4 shrink-0 text-primary" />
                <span className="text-sm">Production Observability</span>
              </div>
              <p className="text-xs leading-relaxed text-text-muted">
                Native SSE streaming, state replay from any checkpoint, and OpenTelemetry tracing
                across OpenAI, Anthropic, Azure, and Google models.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
